import store from "@/store/store";
import { updateDiscounts } from "@/slices/discount-slice";
import { manualSelectDiscount } from "@/domain/discounts/discount-selection-commands";
import { releaseCheckoutPaymentSession } from "@/utils/release-checkout-payment-session";
import { invalidateQueryPrefix } from "@/lib/safe-query/run-safe-query";
import { QUERY_KEY_PREFIX } from "@/query-keys/constants";
import {
  invalidateAvailableCouponsCache,
  invalidateMenuCatalogCache,
} from "@/services/client-query-cache";
import { clearDestinationSession } from "@/domain/destination/destination-session";
import { clearCheckoutAddressForSession } from "@/slices/checkout-address-slice";
import { resetCheckoutFulfilmentForSession } from "@/slices/checkout-ux-slice";
import { clearOrderNotificationsMarker } from "@/services/order-notifications";

/**
 * Clears cart, discounts, and auth-scoped query memos after session invalidation.
 * Pricing Freshness ADR Phase 1: expire the `pricing-preview` transport memo on logout /
 * session invalidate so a prior session’s Preview cannot be reused after re-login.
 */
export function clearAuthDependentClientState(): void {
  releaseCheckoutPaymentSession({
    reason: "auth_logout",
    dispatch: store.dispatch,
    clearCart: true,
    clearBilling: true,
  });
  store.dispatch(
    updateDiscounts({ list: [], ...manualSelectDiscount(null), listLoading: false })
  );
  invalidateAvailableCouponsCache();
  invalidateMenuCatalogCache();
  invalidateQueryPrefix(QUERY_KEY_PREFIX.orderDetails);
  // Legacy namespace — still used by `use-billing-preview`; keep until that path is gone.
  invalidateQueryPrefix(QUERY_KEY_PREFIX.billingPreview);
  // Live Preview Billing transport memo (ADR Phase 1 Expire).
  invalidateQueryPrefix(QUERY_KEY_PREFIX.pricingPreview);
  invalidateQueryPrefix(QUERY_KEY_PREFIX.checkoutSuccess);
  invalidateQueryPrefix(QUERY_KEY_PREFIX.paymentResult);
  clearDestinationSession();
  // Checkout address selection belongs to the signed-in customer: drop Redux state and
  // every restaurant's restoration mirror so the next sign-in cannot inherit it.
  store.dispatch(clearCheckoutAddressForSession());
  // Same for a Pickup choice: back to Delivery, and no stored choice survives for the next customer.
  store.dispatch(resetCheckoutFulfilmentForSession());
  // This browser is no longer a known RasoiX device — the Order Details opt-in must not
  // claim "enabled" for the next customer who signs in here.
  clearOrderNotificationsMarker();
}
